import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import IMask from 'imask';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment } from '@mui/material';

import { ReactBB } from '@/common/utils/index';

const TextInput = ({
  name,
  label,
  className,
  tip,
  type = 'text',
  required,
  disabled = false,
  rules,
  onChangeFormat = (value) => value,
  InputProps: legacyInputProps,
  InputLabelProps: legacyInputLabelProps,
  inputProps: legacyHtmlInputProps,
  slotProps: externalSlotProps,
  containterSx,
  sx,
  ...props
}) => {
  const formContext = useFormContext() || {};
  const { control } = formContext;

  const ref = useRef(null);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (props.mask) {
      IMask(ref.current, {
        mask: props.mask,
      });
    }
  }, [props.mask]);

  const onMouseDown = useCallback((e) => e.preventDefault(), []);
  const onShowPass = useCallback(() => setShowPass((prev) => !prev), []);
  const inputSlotProps = useMemo(() => {
    const res = {
      ...(externalSlotProps?.input || {}),
      ...legacyInputProps,
    };
    if (type === 'password')
      res.endAdornment = (
        <>
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={onShowPass}
              onMouseDown={onMouseDown}
              edge="end"
            >
              {showPass ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        </>
      );
    return res;
  }, [
    externalSlotProps?.input,
    legacyInputProps,
    onShowPass,
    onMouseDown,
    type,
    showPass,
  ]);

  const inputProps = useMemo(
    () => ({ maxLength: 500, ...legacyHtmlInputProps, 'data-testid': name }),
    [legacyHtmlInputProps, name]
  );
  const slotProps = useMemo(
    () => ({
      ...externalSlotProps,
      input: inputSlotProps,
      inputLabel: {
        ...(externalSlotProps?.inputLabel || {}),
        shrink: true,
        sx: {
          mb: 2,
          ...(externalSlotProps?.inputLabel?.sx || {}),
          ...(legacyInputLabelProps?.sx || {}),
        },
        ...legacyInputLabelProps,
      },
      htmlInput: {
        ...(externalSlotProps?.htmlInput || {}),
        ...inputProps,
      },
    }),
    [externalSlotProps, inputProps, inputSlotProps, legacyInputLabelProps]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      className={className}
      sx={containterSx}
    >
      <Controller
        defaultValue=""
        name={name}
        control={control}
        rules={rules}
        render={({
          field: { onChange, value, name },
          fieldState: { error },
        }) => {
          const _disabled =
            typeof disabled === 'function'
              ? disabled(name, formContext)
              : disabled;
          const syncFormValue = (evt) => {
            onChange({
              ...evt,
              target: {
                ...evt.target,
                value: onChangeFormat(evt.target.value),
              },
            });
          };

          return (
            <>
              <TextField
                name={name}
                label={label && type !== 'hidden' ? ReactBB(label) : undefined}
                required={required}
                helperText={error?.message || tip}
                error={!!error}
                fullWidth
                disabled={_disabled}
                sx={[
                  {
                    display: 'flex',
                    flexDirection: 'column',
                  },
                  ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                {...props}
                onChange={syncFormValue}
                onBlur={(evt) => {
                  syncFormValue(evt);
                  props.onBlur?.(evt);
                }}
                value={value}
                slotProps={{
                  ...slotProps,
                  htmlInput: {
                    ...slotProps.htmlInput,
                    // Attach to the <input> itself: on TextField it would land on
                    // the root div and catch bubbled events from adornments
                    // (e.g. a checkbox, whose value is "on").
                    onInput: (evt) => {
                      syncFormValue(evt);
                      props.onInput?.(evt);
                    },
                  },
                }}
                type={
                  type === 'password' ? (showPass ? 'text' : 'password') : type
                }
                inputRef={ref}
              />
            </>
          );
        }}
      />
    </Box>
  );
};

TextInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  className: PropTypes.string,
};

export default TextInput;
